import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import VehicleSize from './VehicleSize'
import Image from 'next/image';
import { ICONS } from '@/constants/constants';

interface VehicleSelectorProps {
    onVehicleSelect: (vehicles: string) => void;
    onVehicleDataUpdate?: (data: { type: string; sameEachWay: boolean; height?: string; length?: string }) => void;
    onClose: () => void;
    triggerElement: HTMLElement | null;
    currentVehicles: string;
    departingDate: string;
}

const VehicleSelector = ({ onVehicleSelect, onVehicleDataUpdate, onClose, triggerElement, currentVehicles, departingDate }: VehicleSelectorProps) => {
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const [isPositioned, setIsPositioned] = useState(false)
    const [sameEachWay, setSameEachWay] = useState(true)
    const [selectedVehicleType, setSelectedVehicleType] = useState(() => {
        // Set initial vehicle type based on current vehicle count
        return currentVehicles === '0' ? 'Foot Passenger' : 'Car'
    })
    const [showVehicleDropdown, setShowVehicleDropdown] = useState(false)
    const [showVehicleSize, setShowVehicleSize] = useState(false)
    const [vehicleSizeTrigger, setVehicleSizeTrigger] = useState<HTMLElement | null>(null)
    const selectorRef = useRef<HTMLDivElement>(null)

    const vehicleTypes = [
        "Foot Passenger",
        "Car",
        "Van",
        "Minivan",
        "Motorhome",
   
       
    ]

    // Calculate position based on trigger element
    useEffect(() => {
        if (triggerElement) {
            // Small delay to ensure smooth positioning
            requestAnimationFrame(() => {
                const rect = triggerElement.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight
                
                // Estimate dropdown width based on screen size
                let dropdownWidth = 500 // default for xl screens
                if (viewportWidth < 640) dropdownWidth = viewportWidth * 0.95 // sm: 95vw
                else if (viewportWidth < 768) dropdownWidth = viewportWidth * 0.85 // md: 85vw
                else if (viewportWidth < 1024) dropdownWidth = viewportWidth * 0.7 // lg: 70vw
                else if (viewportWidth < 1280) dropdownWidth = viewportWidth * 0.5 // xl: 50vw
                else dropdownWidth = Math.min(viewportWidth * 0.4, 512) // xl: 40vw, max 512px
                
                let left = rect.left + scrollLeft
                let top = rect.bottom + scrollTop + 4
                
                // Adjust horizontal position if dropdown would overflow
                if (left + dropdownWidth > viewportWidth) {
                    left = Math.max(8, viewportWidth - dropdownWidth - 8) // 8px margin from edge
                }
                
                // Adjust vertical position if dropdown would overflow
                const estimatedHeight = 450 // estimated dropdown height
                const spaceBelow = viewportHeight - (rect.bottom - scrollTop)
                const spaceAbove = rect.top - scrollTop
                
                // Only move above if there's insufficient space below AND enough space above
                if (spaceBelow < estimatedHeight && spaceAbove > estimatedHeight) {
                    top = rect.top + scrollTop - estimatedHeight - 4 // Show above trigger
                }
                // Otherwise keep it below (default behavior)

                setPosition({ top, left })
                setIsPositioned(true)
            })
        }
    }, [triggerElement])

    // Close selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                // Also check if the click was on the trigger element
                if (triggerElement && !triggerElement.contains(event.target as Node)) {
                    onClose()
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose, triggerElement])

    const handleVehicleTypeSelect = (vehicleType: string) => {
        setSelectedVehicleType(vehicleType)
        setShowVehicleDropdown(false)

        // Update vehicle data in Redux
        onVehicleDataUpdate?.({
            type: vehicleType,
            sameEachWay: sameEachWay
        })

        // Update the vehicle count based on selection
        if (vehicleType === 'Foot Passenger') {
            onVehicleSelect('0')
            onClose() // Close immediately for foot passenger
        } else {
            onVehicleSelect('1')
            // Show vehicle size selector for vehicles that need sizing
            if ( vehicleType !== 'Motorcycle') {
                // Show VehicleSize component instead of closing
                setVehicleSizeTrigger(triggerElement)
                setShowVehicleSize(true)
            } else {
                onClose() // Close immediately for bicycle and motorcycle
            }
        }
    }

    const handleVehicleSizeSelect = (size: { height: string; length: string }) => {
        // Update vehicle data with the selected size
        onVehicleDataUpdate?.({
            type: selectedVehicleType,
            sameEachWay: sameEachWay,
            height: size.height.toLowerCase(),
            length: size.length.toLowerCase()
        })

        setShowVehicleSize(false)
        onClose()
    }

    const formatDate = (dateString: string) => {
        try {
            const [day, month, year] = dateString.split('/')
            const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
            const dayName = dayNames[date.getDay()]
            return `${dayName}, ${day}/${month}`
        } catch {
            return dateString
        }
    }

    const selectorContent = isPositioned && !showVehicleSize ? (
        <div
            ref={selectorRef}
            className="absolute bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] p-5 w-[95vw] sm:w-[85vw] md:w-[70vw] lg:w-[50vw] xl:w-[40vw] max-w-lg"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className='flex gap-3'>
                    <div className="flex flex-col items-center justify-center mb-1">
                        <span className="text-gray-600">Vehicle</span>
                        <h3 className="text-lg font-medium text-gray-900">{selectedVehicleType}</h3>
                    </div>

                </div>
                <div className="text-right flex flex-row-reverse gap-2">
                    <div className="flex flex-col items-center  text-gray-600 mb-1">

                        <span className="text-sm">Departing</span>
                        <p className="text-lg font-normal text-gray-900">{formatDate(departingDate)}</p>
                    </div>

                    <Image src={ICONS.vcalendar} alt='vacalnedar' height={30} width={20} />
                </div>


            </div>

            <h2 className="text-xl font-rakesly font-bold text-gray-900 mb-2">VEHICLE DETAILS</h2>
            <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                Please select your vehicle type. Please Include roof boxes and bikes in your vehicles total height.
            </p>

            {/* Same each way toggle */}
            <div className="flex items-center gap-3 mb-6 p-4 bg-gray-50 rounded-lg">
                <button
                    onClick={() => {
                        const newSameEachWay = !sameEachWay
                        setSameEachWay(newSameEachWay)

                        // Update vehicle data in Redux when toggle changes
                        onVehicleDataUpdate?.({
                            type: selectedVehicleType,
                            sameEachWay: newSameEachWay
                        })
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${sameEachWay ? 'bg-red-500' : 'bg-gray-300'
                        }`}
                >
                    <div
                        className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${sameEachWay ? 'translate-x-6' : 'translate-x-0.5'
                            }`}
                    />
                </button>
                <span className="text-gray-700 font-medium">Same each way</span>
            </div>

            {/* Vehicle type selection */}
            <div className="">
                <label className="block text-gray-700 font-medium mb-2">Travelling By</label>
                <div className="relative">
                    <button
                        onClick={() => setShowVehicleDropdown(!showVehicleDropdown)}
                        className="w-full p-3 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                    >
                        <span className="text-gray-900">{selectedVehicleType}</span>
                        <Image
                            src={ICONS.downarrow}
                            alt='dropdown arrow'
                            height={15}
                            width={15}
                            className={`transition-transform duration-200 ${showVehicleDropdown ? 'rotate-180' : 'rotate-0'}`}
                        />

                    </button>

                    {showVehicleDropdown && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                            {vehicleTypes.map((vehicleType, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                                    onClick={() => handleVehicleTypeSelect(vehicleType)}
                                >
                                    <span className="text-gray-700">{vehicleType}</span>
                                    {selectedVehicleType === vehicleType && (
                                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>



        </div>
    ) : null

    const vehicleSizeContent = showVehicleSize ? (
        <VehicleSize
            triggerElement={vehicleSizeTrigger}
            onSizeSelect={handleVehicleSizeSelect}
            onClose={() => setShowVehicleSize(false)}
        />
    ) : null

    return typeof window !== 'undefined'
        ? createPortal(
            <>
                {selectorContent}
                {vehicleSizeContent}
            </>,
            document.body
        )
        : null
}

export default VehicleSelector